import { z } from 'zod'

/**
 * Label Designer v1 (#1069) — the platform port of the toolkit `label-designer`
 * module: per-term badge styling on the explorer card.
 *
 * Toolkit data model (includes/Config.php, Labels/CssGenerator.php): per-term
 * meta `show_label` + sparse `hrtk_label_style`, per-taxonomy default option,
 * global default option; cascade term → taxonomy → global → built-in, merged key
 * by key (wp_parse_args). Delivery there is a hashed CSS file + a JS shim; here
 * the card computes inline styles from the same cascade (label-style.ts).
 *
 * Keys are NUMERIC Central term ids inside a dimension — the wire carries no
 * slugs (central#2615 `{id,name}[]`), so existing WP term meta cannot migrate;
 * operators re-pick terms. Six dimensions the card payload carries with ids
 * are offered: groups/tags/locations/amenities ship as term ARRAYS
 * (`{id,name}[]`); property_type / property_city (v2, #1364) ship as
 * singular `{id,name}|null` relations as of central#3137 — wrapped into a
 * one-element list by `termsForDimension` in label-style.ts. Older payloads
 * or a null relation degrade to no badge for that dimension, never a crash.
 * property_state / discover_tag / property_flag still have no Central
 * relation and are not offered.
 *
 * Every leaf is lenient: a blank clears, an invalid value drops, a bad
 * override row drops — the card never bricks on an authored value (the
 * quick-filter-chips precedent in config.ts).
 */
export const labelDimensions = ['groups', 'tags', 'locations', 'amenities', 'types', 'cities'] as const
export type LabelDimension = (typeof labelDimensions)[number]

/**
 * Badge placement (v3 slice 1, #1370). Toolkit Config.php keeps `position`
 * (default 'top-left'; presets incl. center-top / center-bottom) and
 * `offset_x` / `offset_y` (-30..30 around a 6px base) on every style layer,
 * and CssGenerator flips the whole row into per-badge absolute positioning as
 * soon as any layer leaves the default. Here the mode is EXPLICIT
 * (`placement`), the corners are the four the card photo has, and `offset` is
 * the absolute inset from both edges of that corner — default 6 = the toolkit
 * base = today's row inset (.hfse-property-groups top/left 6px). The leaves
 * live on the style object so they cascade term → dimension → global exactly
 * like colour; `corner` / `offset` alone never switch a badge to overlay.
 */
export const labelPlacements = ['inline', 'overlay'] as const
export type LabelPlacement = (typeof labelPlacements)[number]
export const labelCorners = ['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const
export type LabelCorner = (typeof labelCorners)[number]
export const LABEL_OVERLAY_OFFSET_DEFAULT = 6
export const LABEL_OVERLAY_OFFSET_MAX = 48

// #RGB / #RRGGBB / #RRGGBBAA — the dashboard color picker emits 8-digit hexa.
const HEX_COLOR_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i

// Hand-rolled type gates (NOT safeParse — the render-boundary lint restricts
// that to config.ts preprocess wrappers): each gate normalizes an authored
// value to a valid one or to undefined, so the strict inner schema never sees
// a rejectable value and an authored blob can never brick the card (#1168).
const gateNumber = (v: unknown): number | undefined => {
  if (typeof v === 'string' && v.trim() !== '') v = Number(v)
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}
const optNum = (min: number, max: number, int = true) =>
  z.preprocess((v) => {
    const n = gateNumber(v)
    if (n === undefined || (int && !Number.isInteger(n)) || n < min || n > max) return undefined
    return n
  }, z.number().min(min).max(max).optional())
const optEnum = <U extends string, T extends readonly [U, ...U[]]>(values: T) =>
  z.preprocess((v) => ((values as readonly string[]).includes(v as string) ? v : undefined), z.enum(values).optional())
const optPattern = (re: RegExp, maxLen = 500) =>
  z.preprocess(
    (v) => (typeof v === 'string' && v.trim().length <= maxLen && re.test(v.trim()) ? v.trim() : undefined),
    z.string().optional(),
  )
/** Boolean leaf: true/false or the form strings; anything else is unset (never a failed row). */
const optBool = z.preprocess(
  (v) => (typeof v === 'boolean' ? v : v === 'true' ? true : v === 'false' ? false : undefined),
  z.boolean().optional(),
)
/** Non-plain-object containers degrade to undefined instead of failing the card parse. */
const objectOrUndef = (v: unknown) => (typeof v === 'object' && v !== null && !Array.isArray(v) ? v : undefined)

export const labelStyleSchema = z.preprocess(objectOrUndef, z.object({
  bgColor: optPattern(HEX_COLOR_RE, 12).describe('Badge background (hex; #RRGGBBAA allowed)'),
  textColor: optPattern(HEX_COLOR_RE, 12).describe('Badge text color (hex). Empty = automatic black or white against the background'),
  fontSize: optNum(8, 32).describe('Font size in px'),
  fontWeight: optEnum(['400', '500', '600', '700']).describe('Font weight'),
  letterSpacing: optNum(-2, 10, false).describe('Letter spacing in px'),
  textTransform: optEnum(['none', 'uppercase', 'lowercase', 'capitalize']).describe('Text transform'),
  borderRadius: optNum(0, 64).describe('Corner radius in px'),
  paddingH: optNum(0, 32).describe('Horizontal padding in px'),
  paddingV: optNum(0, 32).describe('Vertical padding in px'),
  iconUrl: optPattern(/^https:\/\//).describe('Optional https icon image shown before the label text'),
  // Icon recolor / icon-only (v3 slice 2, #1370). Toolkit Config.php keeps
  // `recolor_icon` (default on, honoured for .svg URLs only: CssGenerator masks
  // the icon with the badge currentColor) and `icon_only` (default off:
  // font-size:0 collapses the text) on every style layer. Both cascade here
  // like colour; UNSET = today's rendering (plain <img>, text shown) so an
  // existing feed with an iconUrl is byte-for-byte unchanged.
  recolorIcon: optBool.describe('Tint an .svg icon to the badge text colour (toolkit "Recolor SVG to match text color"); raster icons (png, jpg) show as-is. Needs an icon URL. Empty = off'),
  iconOnly: optBool.describe('Show only the icon and hide the label text (toolkit "Hide label text"); the term name stays as the accessible name. Needs an icon URL. Empty = off'),
  placement: optEnum(labelPlacements).describe('Where the badge sits: inline = in the label row at the top-left of the card (default); overlay = pinned to a corner of the card photo'),
  corner: optEnum(labelCorners).describe('Photo corner for an overlay badge. Empty = top-left. Ignored when placement is inline'),
  offset: optNum(0, LABEL_OVERLAY_OFFSET_MAX).describe(`Overlay inset from both edges of that corner in px (0-${LABEL_OVERLAY_OFFSET_MAX}). Empty = ${LABEL_OVERLAY_OFFSET_DEFAULT}. Ignored when placement is inline`),
// `.optional()` must sit INSIDE the preprocess: ZodOptional short-circuits on the
// RAW input, so a gate that returns undefined would otherwise hit a required object.
}).optional())
export type LabelStyle = NonNullable<z.infer<typeof labelStyleSchema>>

export const termLabelOverrideSchema = z.object({
  filterKey: z.enum(labelDimensions).describe('Taxonomy dimension of the term'),
  // gateNumber (not z.coerce): Number([7]) === 7, so bare coercion accepts a
  // wrong-shaped authored value; a non-numeric here must FAIL the row so the
  // config.ts lenient wrapper drops it.
  centralId: z.preprocess((v) => gateNumber(v) ?? v, z.number().int().positive()).describe('Resolved Central term ID'),
  style: labelStyleSchema.optional().describe('Style for this term; empty leaves inherit the dimension / global defaults'),
})
export const LABEL_OVERRIDES_MAX = 50

export const labelDefaultsSchema = z.preprocess(objectOrUndef, z.object({
  groups: labelStyleSchema.optional().describe('Default style for Group badges'),
  tags: labelStyleSchema.optional().describe('Default style for Tag badges'),
  locations: labelStyleSchema.optional().describe('Default style for Region badges'),
  amenities: labelStyleSchema.optional().describe('Default style for Amenity badges'),
  types: labelStyleSchema.optional().describe('Default style for Property Type badges'),
  cities: labelStyleSchema.optional().describe('Default style for City badges'),
}).optional())

/**
 * The four `card.*` label fields. The STRICT override array lives here; the
 * render-lenient row-dropping wrapper (safeParse) is built in
 * widgets/explorer/config.ts — the sanctioned location per the render-boundary
 * lint (chips / marketing-cards precedent) — and overrides `labelOverrides`
 * when spread into the card schema.
 */
/**
 * Card tags (#1812) — the platform port of the toolkit Label Designer "Card
 * tags" section (homerunner-toolkit 3.326, includes/Labels/CardTags.php,
 * toolkit#1038): up to N priority-ordered tag pills in the card's
 * property-type slot, each carrying a singular card label ("Pools" → "Private
 * Pool"). The toolkit rewrites the REST `types` field server-side; here the
 * card resolves the same list from the `tags` / `amenities` term arrays the
 * public payload already carries (central#2615 `{id,name}[]`), so the data
 * never changes shape and analytics keep reading the real property type.
 *
 * Knob map (toolkit option `hrtk_card_tags` → `card.cardTags`): enabled →
 * enabled; taxonomy (property_tag | amenity; property_type excluded, it is
 * the slot the tags replace) → source; terms [{slug,label}] in priority order
 * → terms [{centralId,label}]; limit 1..10 (3) → limit; keep_type → keepType;
 * pills → pills; own_row → ownRow; style {bg,text,border,radius,size} →
 * style. `preserve_type` has no platform equivalent: the type is a separate
 * field here, nothing is rewritten, so it is always preserved.
 *
 * Every leaf is lenient (the Label Designer precedent above): absent = the
 * toolkit default at the point of use, invalid = absent, so an authored blob
 * can never brick the card. The strict row schema lives here; the
 * row-dropping render wrapper is built in widgets/explorer/config.ts.
 */
export const cardTagSources = ['tags', 'amenities'] as const
export type CardTagSource = (typeof cardTagSources)[number]
export const CARD_TAG_TERMS_MAX = 50
export const CARD_TAG_LIMIT_MIN = 1
export const CARD_TAG_LIMIT_MAX = 10
export const CARD_TAG_LIMIT_DEFAULT = 3
export const CARD_TAG_LABEL_MAX = 60
/** CardTags::defaults()['style'] verbatim. */
export const CARD_TAG_STYLE_DEFAULTS = {
  bgColor: '#faf8f3',
  textColor: '#333333',
  borderColor: '#e3ddd0',
  borderRadius: 24,
  fontSize: 13,
} as const

export const cardTagTermSchema = z.object({
  centralId: z.preprocess((v) => gateNumber(v) ?? v, z.number().int().positive()).describe('Resolved Central term ID in the chosen source'),
  label: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() !== '' ? v.trim().slice(0, CARD_TAG_LABEL_MAX) : undefined),
    z.string().max(CARD_TAG_LABEL_MAX).optional(),
  ).describe(`Singular card label for this term (max ${CARD_TAG_LABEL_MAX} characters), e.g. "Private Pool" for the tag "Pools". Empty = the term name`),
})
export type CardTagTerm = z.infer<typeof cardTagTermSchema>

export const cardTagStyleSchema = z.preprocess(objectOrUndef, z.object({
  bgColor: optPattern(HEX_COLOR_RE, 12).describe(`Pill background (hex). Empty = ${CARD_TAG_STYLE_DEFAULTS.bgColor}`),
  textColor: optPattern(HEX_COLOR_RE, 12).describe(`Pill text colour (hex). Empty = ${CARD_TAG_STYLE_DEFAULTS.textColor}`),
  borderColor: optPattern(HEX_COLOR_RE, 12).describe(`Pill border colour (hex). Empty = ${CARD_TAG_STYLE_DEFAULTS.borderColor}`),
  borderRadius: optNum(0, 999).describe(`Pill corner radius in px (0-999). Empty = ${CARD_TAG_STYLE_DEFAULTS.borderRadius}`),
  fontSize: optNum(10, 20).describe(`Pill font size in px (10-20). Empty = ${CARD_TAG_STYLE_DEFAULTS.fontSize}`),
}).optional())
export type CardTagStyle = NonNullable<z.infer<typeof cardTagStyleSchema>>

export const cardTagsSchema = z.preprocess(objectOrUndef, z.object({
  enabled: optBool.describe('Show tag pills in the card\'s property-type slot. Off (default) leaves the card exactly as it is. Toolkit: Card tags → Enable'),
  source: optEnum(cardTagSources).describe('Which terms the pills come from: the property tags (default) or the amenities. The property type cannot be the source — it is the slot the pills replace. Toolkit: taxonomy'),
  terms: z.array(cardTagTermSchema).max(CARD_TAG_TERMS_MAX).optional().describe(`The terms that may appear on a card, in priority order (first row wins the first pill; max ${CARD_TAG_TERMS_MAX}). A home shows only the listed terms it carries. Toolkit: terms`),
  limit: optNum(CARD_TAG_LIMIT_MIN, CARD_TAG_LIMIT_MAX).describe(`Most pills per card (${CARD_TAG_LIMIT_MIN}-${CARD_TAG_LIMIT_MAX}). Empty = ${CARD_TAG_LIMIT_DEFAULT}. Toolkit: limit`),
  keepType: optBool.describe('A home with none of the listed terms keeps its property type in the slot (default). Off = that slot stays empty. Toolkit: keep_type'),
  pills: optBool.describe('Render each term as a pill (default). Off = one slash-separated line, like the type line today. Toolkit: pills'),
  ownRow: optBool.describe('Legacy card design only: location and tags on two left-aligned rows (default). Off = the tags stay on the right of the location line. Toolkit: own_row'),
  style: cardTagStyleSchema.describe('Pill style. Empty fields keep the toolkit defaults'),
}).optional())
export type CardTagsConfig = NonNullable<z.infer<typeof cardTagsSchema>>

export const cardLabelFields = {
  labelGlobalDefault: labelStyleSchema.optional().describe('Global default badge style (lowest cascade layer above the built-in look)'),
  labelDefaults: labelDefaultsSchema.optional().describe('Per-dimension default badge styles'),
  labelOverrides: z.array(termLabelOverrideSchema).max(LABEL_OVERRIDES_MAX).optional().describe(`Terms that get a badge on the card (max ${LABEL_OVERRIDES_MAX}). A row shows that term; its style cascades over the dimension and global defaults`),
  cardTags: cardTagsSchema.describe('Card tags (#1812, toolkit Label Designer "Card tags"): priority-ordered singular tag pills in the card\'s property-type slot'),
}
export const cardLabelFieldsSchema = z.object(cardLabelFields)
export type CardLabelFields = z.infer<typeof cardLabelFieldsSchema>
export type TermLabelOverride = z.infer<typeof termLabelOverrideSchema>
